/**
 * Drafts and reconciliation (`112-offline-drafts.md` §4, §7).
 *
 * The draft write is the guarantee. It is NOT "the thing that happens if the save fails"
 * — it happens ALWAYS, and it happens FIRST (~500ms, vs the network save's ~800ms). The
 * save being queued, retried or rejected has no effect on it whatsoever. That
 * independence is the whole point: there is no code path in which a server problem can
 * prevent the local write.
 *
 * The worst case this buys us is the loss of ~500ms of typing. Not the last 30 seconds.
 * Not "whatever hadn't autosaved". Half a second.
 */
import type { PageDto, ProseMirrorDoc } from '@/api/endpoints/pages';
import { deepEqual } from '@/lib/content';
import { db, type DraftRecord } from '@/offline/db';

/** Where the losing side of a resolved conflict goes, so that even it is not destroyed. */
const backupKey = (pageId: string): string => `${pageId}:conflict-backup`;

export async function saveDraft(draft: DraftRecord): Promise<void> {
  await (await db()).put('drafts', draft);
}

export async function loadDraft(pageId: string): Promise<DraftRecord | undefined> {
  return (await db()).get('drafts', pageId);
}

export async function deleteDraft(pageId: string): Promise<void> {
  await (await db()).delete('drafts', pageId);
}

/**
 * A save landed. The draft is **not deleted** — it is re-based onto the `updatedAt` the
 * server just gave us, so that a reload finds `draft.baseUpdatedAt === server.updatedAt`
 * and takes the silent `restore`/`discard` path rather than crying conflict at a user who
 * has done nothing wrong (§4, last bullet).
 */
export async function markDraftSynced(pageId: string, updatedAt: string): Promise<void> {
  const draft = await loadDraft(pageId);
  if (draft === undefined) return;
  await saveDraft({ ...draft, baseUpdatedAt: updatedAt });
}

/* ── Reconciliation (§7) ────────────────────────────────────────────────────── */

export type Reconciliation =
  | { action: 'none' }
  | { action: 'discard' }
  | { action: 'restore'; draft: DraftRecord }
  | { action: 'conflict'; draft: DraftRecord; server: PageDto };

/**
 * On mount, `PageView` fetches the page AND loads its draft, and must decide what the
 * user sees. This is the subtle part of the whole document.
 */
export async function reconcile(server: PageDto): Promise<Reconciliation> {
  const draft = await loadDraft(server.id);
  if (draft === undefined) return { action: 'none' };

  // 1. Identical → the draft is the residue of a save that succeeded before the draft got
  //    cleaned up. Delete it. Say nothing.
  if (deepEqual(draft.content, server.content) && draft.title === server.title) {
    await deleteDraft(server.id);
    return { action: 'discard' };
  }

  // 2. The server has not moved since the draft was taken. Nobody else touched the page,
  //    so the draft strictly descends from the server's copy and is strictly newer.
  //    Restore it, no questions asked — no banner, no prompt, no "we recovered your
  //    work!" modal. The user opens the page and their text is simply THERE.
  if (draft.baseUpdatedAt === server.updatedAt) {
    return { action: 'restore', draft };
  }

  // 3. The server is NEWER than the base the draft was taken from. Something else wrote to
  //    this page — another tab, a replayed outbox entry, another device. We have two
  //    divergent versions and NO right to pick one.
  return { action: 'conflict', draft, server };
}

/* ── Conflict resolution (§7.3) ─────────────────────────────────────────────── */

/**
 * "Keep mine". Before we overwrite the server's version, it is written to a backup draft
 * key — so even the LOSING side of a conflict is recoverable. Nothing here is
 * unrecoverable, ever.
 */
export async function backupServerVersion(server: PageDto): Promise<void> {
  await saveDraft({
    pageId: backupKey(server.id),
    content: server.content,
    title: server.title,
    baseUpdatedAt: server.updatedAt,
    savedAt: Date.now(),
  });
}

/** "Use server version" — the losing DRAFT is backed up, so the `[Undo]` toast can work. */
export async function backupDraft(draft: DraftRecord): Promise<void> {
  await saveDraft({ ...draft, pageId: backupKey(draft.pageId) });
}

export async function loadBackup(pageId: string): Promise<DraftRecord | undefined> {
  return loadDraft(backupKey(pageId));
}

/** The `[Undo]` on the "Use server version" toast: put the draft back, as it was. */
export async function restoreBackup(pageId: string): Promise<DraftRecord | undefined> {
  const backup = await loadBackup(pageId);
  if (backup === undefined) return undefined;

  const draft: DraftRecord = { ...backup, pageId };
  await saveDraft(draft);
  await deleteDraft(backupKey(pageId));
  return draft;
}

/** The queued body IS the draft, if none is stored (§7.4). */
export function draftFromContent(
  pageId: string,
  content: ProseMirrorDoc,
  title: string,
  baseUpdatedAt: string,
): DraftRecord {
  return { pageId, content, title, baseUpdatedAt, savedAt: Date.now() };
}
